"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"

interface ChatLauncherContextValue {
  pageInstanceActive: boolean
  setPageInstanceActive: (v: boolean) => void
  /**
   * Monotonic counter of "open the panel now" requests from an AI-answer
   * surface (the search/hero lane, the test-detail section). The mounted
   * `ContextualChat` watches it and opens live.
   *
   * A counter rather than a boolean: two escalations in one page view must both
   * open the panel, and a boolean that's already `true` emits no change.
   */
  openRequests: number
  /** Ask the mounted chat panel to open on THIS page view. */
  requestPanelOpen: () => void
}

const ChatLauncherContext = createContext<ChatLauncherContextValue>({
  pageInstanceActive: false,
  setPageInstanceActive: () => {},
  openRequests: 0,
  requestPanelOpen: () => {},
})

export function ChatLauncherProvider({ children }: { children: ReactNode }) {
  const [pageInstanceActive, setPageInstanceActive] = useState(false)
  const [openRequests, setOpenRequests] = useState(0)
  const requestPanelOpen = useCallback(
    () => setOpenRequests((n) => n + 1),
    [setOpenRequests]
  )
  return (
    <ChatLauncherContext.Provider
      value={{
        pageInstanceActive,
        setPageInstanceActive,
        openRequests,
        requestPanelOpen,
      }}
    >
      {children}
    </ChatLauncherContext.Provider>
  )
}

export function useChatLauncherContext() {
  return useContext(ChatLauncherContext)
}

export function usePageScopedChat(active: boolean): void {
  const { setPageInstanceActive } = useChatLauncherContext()
  useEffect(() => {
    if (active) setPageInstanceActive(true)
    return () => {
      if (active) setPageInstanceActive(false)
    }
  }, [active, setPageInstanceActive])
}
